import React from 'react';
import type { FormField } from '../interfaces';
import { Box, makeStyles, Typography } from '@material-ui/core';
import type { FileObject } from '@klp/file/classes';
import { FileUploadDynamic } from '@klp/file/components';
import { ActionButton, Block } from '@klp/design/index';
import { Add } from '@material-ui/icons';
import {
    useGetProsjektProcessAttachmentsQuery,
    useDeleteProsjektProcessAttachmentMutation,
    useSaveGarantiMutation,
} from '../services/rtkApi';
import { getLocalizedLabel } from '../utils';
import { Vedlegg } from './Vedlegg';
import { useFormikContext } from 'formik';

const useStyles = makeStyles((theme) => ({
    vedleggListContainer: {
        paddingTop: theme.spacing(2),
        paddingBottom: theme.spacing(2),

        '& .MuiTableCell-root': {
            padding: theme.spacing(1),
        },
    },

    actionButtonContainer: {
        display: 'flex',
        justifyContent: 'flex-end',
    },

    labelContainer: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2),
    },

    labelRequired: {
        color: theme.palette.error.main,
        fontSize: '1.5rem',
    },
}));

type GarantiDokumentUploadProps = {
    field: FormField;
    isReadOnly: boolean;
    isRequired: boolean;
    prosessInstansId: string;
};

export const GarantiDokumentUpload = ({
    field,
    isReadOnly,
    isRequired,
    prosessInstansId,
}: GarantiDokumentUploadProps) => {
    const classes = useStyles();
    const { setFieldValue } = useFormikContext();
    const { data: garantiDokumenter } = useGetProsjektProcessAttachmentsQuery({
        prosessInstansId: prosessInstansId,
        vedleggType: 'Garanti',
    });
    const [saveGaranti, { isLoading: isSaving }] = useSaveGarantiMutation();
    const [deleteAttachment, { isLoading: isDeleting }] =
        useDeleteProsjektProcessAttachmentMutation();

    const handleDeleteVedlegg = (vedleggId: string) => {
        deleteAttachment({ attachmentId: vedleggId }).then(() =>
            setFieldValue(field.id, undefined),
        );
    };

    const handleAddVedlegg = (file: FileObject) => {
        const vedlegg = {
            name: file.fileName,
            type: file.type,
            content: file.fileContent,
        };
        saveGaranti({
            prosessInstansId: prosessInstansId,
            garantiFile: vedlegg,
        }).then(() => setFieldValue(field.id, file.fileName));
    };

    return (
        <Box>
            <Block className={classes.labelContainer}>
                <Typography variant="h2">
                    {getLocalizedLabel(
                        `project.formFields.${field.id}`,
                        field.label,
                        field.id,
                    )}
                </Typography>
                {isRequired && (
                    <Typography className={classes.labelRequired}>
                        &nbsp;*
                    </Typography>
                )}
            </Block>
            <Block className={classes.vedleggListContainer}>
                {garantiDokumenter &&
                    garantiDokumenter.length > 0 &&
                    garantiDokumenter.map((document) => (
                        <Vedlegg
                            key={`${document.id}`}
                            vedlegg={document}
                            isReadOnly={isReadOnly}
                            onDeleteRow={() =>
                                handleDeleteVedlegg(document.id.toString())
                            }
                            isDeleting={isDeleting}
                        />
                    ))}
            </Block>
            {!isReadOnly && (
                <Block className={classes.actionButtonContainer}>
                    <FileUploadDynamic
                        mimeTypes="application/pdf"
                        queueName="garanti-dokument"
                        id="garanti-dokument-uploader"
                        fileUploadCallback={handleAddVedlegg}
                        disabled={isSaving}
                    >
                        <ActionButton
                            actionType="button"
                            translation="prosjekt.vedlegg.add"
                            isLoading={isSaving}
                            endIcon={<Add />}
                        />
                    </FileUploadDynamic>
                </Block>
            )}
        </Box>
    );
};
